// utils/upgradePopup.ts
//
// Browser side of the Free/Pro rules. The SERVER decides everything (plan,
// counters, whether a popup may show). This file only asks and displays.
// With MONETIZATION_MODE=off on the server nothing here has any effect.
import { api } from "../services/api";
import { getAppLanguage } from "./appLanguage";
import { getUpgradePopupText } from "./upgradePopupText";

export const UPGRADE_POPUP_EVENT = "astria:upgrade-popup";

/** Moments the spec allows a popup for (see masterConfig.json upsellRules.showWhen). */
export type UpgradePopupTrigger =
  | "user_opens_locked_content"
  | "user_clicks_growth_edge"
  | "user_clicks_current_season"
  | "user_clicks_full_chart"
  | "user_clicks_full_personality"
  | "user_clicks_life_blueprint"
  | "companion_limit_reached";

export interface UpgradePopupRequest {
  trigger: UpgradePopupTrigger;
  feature?: string;
  placement?: string;
}

/** One id per browser tab session; the server uses it for "max 1 popup per session". */
export const getPopupSessionId = (): string => {
  const KEY = "astria_popup_session";
  try {
    let id = sessionStorage.getItem(KEY);
    if (!id) {
      id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    return "no-storage";
  }
};

/**
 * Ask for the upgrade popup. Safe to call from anywhere on any trigger:
 * the server applies the caps (1 per session, 3 per day, 30 minute wait) and
 * the popup shows only if it says yes. Never throws.
 */
export const requestUpgradePopup = (request: UpgradePopupRequest): void => {
  try {
    window.dispatchEvent(new CustomEvent<UpgradePopupRequest>(UPGRADE_POPUP_EVENT, { detail: request }));
  } catch {
    /* a popup request must never break the screen that asked */
  }
};

/** Free Companion Talk counter from the reply headers (null when not a limited free reply). */
export interface TalkLimitInfo {
  used: number;
  limit: number;
  warning: "soft" | "limit" | null;
  /** True when the server detected a deep-pattern question (recurring patterns, identity, etc.) */
  deepPattern: boolean;
}

export const readTalkLimitHeaders = (get: (name: string) => string | null | undefined): TalkLimitInfo | null => {
  const used = Number(get("x-talk-used"));
  const limit = Number(get("x-talk-limit"));
  if (!Number.isFinite(used) || !Number.isFinite(limit) || !limit) return null;
  const warning = get("x-talk-warning");
  const deepPattern = get("x-talk-deep") === "true";
  return { used, limit, warning: warning === "soft" || warning === "limit" ? warning : null, deepPattern };
};

/** True when a failed chat call was the free question limit. */
export const isTalkLimitError = (data: any): boolean => data?.code === "TALK_LIMIT_REACHED";

/** Best-effort analytics; never throws and never blocks the UI. */
export const reportAnalyticsEvent = (name: "locked_content_opened" | "upsell_clicked", props: Record<string, string> = {}): void => {
  api.post("/backend/monetization/analytics-event", { name, ...props }, { skipAuthRedirect: true }).catch(() => {});
};

/** Window events the chat screen listens to. Inline signals only; neither opens a popup. */
export const TALK_DEEP_EVENT = "astria:talk-deep";
export const TALK_NOTE_EVENT = "astria:talk-note";

/** Delay so the finished answer is on screen before the modal opens. */
export const LIMIT_POPUP_DELAY_MS = 600;

/** "You have 2 free conversations remaining today." (shown inline after conversation 10). */
export const remainingConversationsMessage = (meter: Pick<TalkLimitInfo, "used" | "limit">): string => {
  const remaining = meter.limit - meter.used;
  if (remaining === 2) return "You have 2 free conversations remaining today.";
  if (remaining === 1) return "You have 1 free conversation remaining today.";
  return getUpgradePopupText(getAppLanguage()).soft;
};

/**
 * Turn the server's counter into UI signals, AFTER the full answer was delivered:
 *  - conversation 10 (soft): an inline note under the answer, no popup
 *  - deep-pattern question: a subtle inline Pro line, no popup
 *  - conversation 12 (limit): ask for the upgrade modal (server still applies
 *    1 per session / 3 per day and never shows it to Pro users)
 * Never throws.
 */
export const applyTalkMeter = (meter: TalkLimitInfo | null): void => {
  if (!meter) return;
  try {
    if (meter.warning === "soft") {
      window.dispatchEvent(new CustomEvent(TALK_NOTE_EVENT, { detail: { text: remainingConversationsMessage(meter) } }));
    }
    if (meter.deepPattern) {
      window.dispatchEvent(new Event(TALK_DEEP_EVENT));
    }
    if (meter.warning === "limit") {
      setTimeout(
        () => requestUpgradePopup({ trigger: "companion_limit_reached", placement: "talk_post_response" }),
        LIMIT_POPUP_DELAY_MS,
      );
    }
  } catch {
    /* a signal must never break the chat */
  }
};
